import { Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { Driver, DriveStep, driver } from 'driver.js';

import { FeatureCatalogEntry, FeatureTourStep } from '../../feature-catalog/feature-catalog.model';

/** How long a step waits for its element to render (lazy routes, loading spinners). */
const ELEMENT_TIMEOUT_MS = 4000;

/**
 * Runs the guided tours of the Funktionskatalog on top of driver.js. Steps may live on
 * different routes: before a step is shown the tour navigates there and waits until the
 * element is rendered. Optional steps whose element never shows up are skipped; a required
 * step without its element is shown as a centered popover instead of failing.
 */
@Injectable({ providedIn: 'root' })
export class FeatureTourService {
  private activeDriver: Driver | null = null;

  constructor(
    private readonly router: Router,
    private readonly zone: NgZone,
  ) {}

  public async start(feature: FeatureCatalogEntry): Promise<void> {
    const steps = feature.tour;
    if (!steps?.length) return;

    this.activeDriver?.destroy();

    const tour = driver({
      showProgress: true,
      progressText: '{{current}} von {{total}}',
      nextBtnText: 'Weiter',
      prevBtnText: 'Zurück',
      doneBtnText: 'Fertig',
      popoverClass: 'vinlivt-tour',
      stagePadding: 6,
      stageRadius: 12,
      overlayOpacity: 0.55,
      steps: steps.map((step) => this.toDriveStep(step)),
      onNextClick: () => this.zone.run(() => this.move(tour, steps, 1)),
      onPrevClick: () => this.zone.run(() => this.move(tour, steps, -1)),
      onDestroyed: () => {
        if (this.activeDriver === tour) this.activeDriver = null;
      },
    });
    this.activeDriver = tour;

    const first = await this.findShowableIndex(steps, 0, 1);
    if (first === null || this.activeDriver !== tour) return;
    tour.drive(first);
  }

  private async move(tour: Driver, steps: FeatureTourStep[], direction: 1 | -1): Promise<void> {
    const current = tour.getActiveIndex() ?? 0;
    const target = await this.findShowableIndex(steps, current + direction, direction);
    if (this.activeDriver !== tour) return;

    if (target === null) {
      // Moving forward past the last step finishes the tour; moving back past the first stays put.
      if (direction === 1) tour.destroy();
      return;
    }
    tour.moveTo(target);
  }

  /** Prepares steps from `index` on in `direction` and returns the first one that can be shown. */
  private async findShowableIndex(steps: FeatureTourStep[], index: number, direction: 1 | -1): Promise<number | null> {
    for (let i = index; i >= 0 && i < steps.length; i += direction) {
      const found = await this.prepare(steps, i);
      if (found || !steps[i].optional) return i;
    }
    return null;
  }

  /** Navigates to the step's route and waits for its element. Returns whether the element is there. */
  private async prepare(steps: FeatureTourStep[], index: number): Promise<boolean> {
    const route = this.routeOf(steps, index);
    if (route && this.currentPath() !== route) {
      await this.router.navigateByUrl(route);
    }

    const selector = steps[index].element;
    if (!selector) return true;
    return (await this.waitForVisible(selector)) !== null;
  }

  /** The step's own route, or the closest route defined by an earlier step. */
  private routeOf(steps: FeatureTourStep[], index: number): string | undefined {
    for (let i = index; i >= 0; i--) {
      if (steps[i].route) return steps[i].route;
    }
    return undefined;
  }

  private currentPath(): string {
    return this.router.url.split(/[?#]/)[0];
  }

  private toDriveStep(step: FeatureTourStep): DriveStep {
    return {
      // Resolved lazily when the step is shown; the same marker may exist twice (mobile + desktop variant).
      element: step.element ? () => this.findVisible(step.element!) as Element : undefined,
      popover: { title: step.title, description: step.text },
    };
  }

  private waitForVisible(selector: string): Promise<HTMLElement | null> {
    const deadline = Date.now() + ELEMENT_TIMEOUT_MS;

    return new Promise((resolve) => {
      const check = (): void => {
        const element = this.findVisible(selector);
        if (element || Date.now() > deadline) {
          resolve(element);
          return;
        }
        setTimeout(check, 100);
      };
      check();
    });
  }

  private findVisible(selector: string): HTMLElement | null {
    const candidates = Array.from(document.querySelectorAll<HTMLElement>(selector));
    return candidates.find((element) => element.getClientRects().length > 0) ?? null;
  }
}
